import React from 'react';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { NotificationsCenterQuery } from '@reverbdotcom/commons/src/gql/graphql';

export const NOTIFICATION_EVENT_TRACKING_FRAGMENT = gql(`
  fragment NotificationEventTrackingFields on NotificationEventTracking {
    notificationType
    notificationId
    name
    position
    listingId
    cpId
    discountAmountCents
    discountAmountCurrency
    discountPercent
  }
`);

export const NOTIFICATION_ACTION_FRAGMENT = gql(`
  fragment NotificationActionFields on NotificationAction {
    key
    title
    description
    severity
    iconName
    href
    links {
      label
      url
    }
    eventTracking {
      ...NotificationEventTrackingFields
    }
  }
`);

export const NOTIFICATIONS_CENTER_QUERY = gql(`
  query NotificationsCenter($after: String) {
    notificationActions {
      rollup {
        ...NotificationActionFields
      }
      actions {
        ...NotificationActionFields
      }
    }
    notificationTasks {
      tasks {
        category
        title
        iconName
        unreadCount
        href
        eventTracking {
          ...NotificationEventTrackingFields
        }
      }
    }
    notificationStream(after: $after, limit: 25) {
      totalUnread
      nextCursor
      items {
        id
        kind
        severity
        title
        body
        eyebrow
        href
        iconName
        thumbnailUrl
        createdAt
        expiresAt
        isRead
        eventTracking {
          ...NotificationEventTrackingFields
        }
      }
    }
  }
`);

/**
 * Appends a stream page onto the one already in the cache. The cursor and the unread total
 * come from the incoming page - they describe the stream as of that fetch - while the items
 * accumulate, so the panel keeps everything the user has scrolled past.
 */
export function appendStreamPage(
  previous: NotificationsCenterQuery,
  next?: NotificationsCenterQuery,
): NotificationsCenterQuery {
  if (!next?.notificationStream) { return previous; }

  return {
    ...previous,
    notificationStream: {
      ...next.notificationStream,
      items: [
        ...previous.notificationStream.items,
        ...next.notificationStream.items,
      ],
    },
  };
}

export function useNotificationsCenter(isOpen: boolean) {
  // One fetch per mount: the API sorts unread items first, so refetching on each open would
  // reorder the list under the reader and drop whatever `fetchMore` had appended.
  // `nextFetchPolicy` can't hold this - `useQuery` writes an explicit `fetchPolicy` back into
  // the observable every time it unskips.
  const { data, loading, fetchMore } = useQuery(NOTIFICATIONS_CENTER_QUERY, {
    skip: !isOpen,
    fetchPolicy: 'cache-first',
    variables: { after: null },
  });

  // A ref rather than state: two calls in the same tick would close over the same
  // pre-render state value, so a state flag lets the second one through and the page appends
  // twice. Apollo dedupes the request itself, so both `updateQuery` callbacks still run.
  const isLoadingMoreRef = React.useRef(false);
  const nextCursor = data?.notificationStream?.nextCursor ?? null;

  // A null cursor is the end of the stream, and a fetch already in flight owns the next
  // page - the sentinel can report itself in view repeatedly while one is resolving.
  // Identity turns over when the cursor advances, which is what lets the sentinel ask again
  // for the page after this one without waiting on another scroll.
  const loadMoreStream = React.useCallback(() => {
    if (!nextCursor || isLoadingMoreRef.current) { return; }

    isLoadingMoreRef.current = true;

    fetchMore({
      variables: { after: nextCursor },
      updateQuery: (previous, { fetchMoreResult }) => appendStreamPage(previous, fetchMoreResult),
    })
      // Swallowed so the panel stays usable. The cursor hasn't moved, so nothing re-asks
      // until the sentinel leaves and re-enters the viewport.
      .catch(() => {})
      .finally(() => { isLoadingMoreRef.current = false; });
  }, [nextCursor, fetchMore]);

  return {
    data,
    loading,
    loadMoreStream,
    hasMoreStream: !!nextCursor,
  };
}
